import { usePlayerStore } from '../stores'
import { ChevronRight, Share2, Globe, Database, Users, Activity, ShieldCheck, Layers3, BarChart3 } from 'lucide-react'
import { COMPETITION_POLICY_NOTICE } from '../utils/competitions'


function LandingPage({ onLoginClick, onExplore, onNavigate }: { onLoginClick: () => void; onExplore: () => void; onNavigate: (view: string) => void }) {
  const { players } = usePlayerStore()
  const featuredPlayers = [...players]
    .sort((a, b) => a.age - b.age)
    .slice(0, 4)

  return (
    <div className="min-h-screen bg-[#050910] text-white overflow-hidden">
      {/* Background decoration */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-green-500/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/10 blur-[120px] rounded-full" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-20 space-y-32">
        {/* Hero Section */}
        <section className="flex flex-col lg:flex-row items-center gap-12 pt-8">
          <div className="flex-1 space-y-8 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-[10px] uppercase tracking-[0.2em] font-bold">
              <img src="/scoutang-logo.svg" alt="" className="w-3 h-3" />
              Nigeria's Football Ecosystem
            </div>
            
            <h1 className="text-5xl md:text-7xl font-display font-bold leading-[1.1]">
              Players, Scouts and Clubs <span className="text-green-500">on one platform</span>
            </h1>
            
            <p className="text-xl text-slate-100 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              Scouta connects players, scouts, and clubs in one platform. Players manage their profiles and career data. Scouts discover talent. Clubs recruit with confidence.
            </p>
            <div className="max-w-2xl mx-auto lg:mx-0 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200 leading-relaxed">
              {COMPETITION_POLICY_NOTICE}
            </div>

            <div className="flex flex-wrap justify-center lg:justify-start gap-4">
              <button 
                onClick={onLoginClick} 
                className="btn-primary px-8 py-4 text-lg shadow-lg shadow-green-500/20"
              >
                Join Scouta
              </button>
              <button 
                onClick={() => onNavigate('how-it-works')} 
                className="btn-secondary px-8 py-4 text-lg"
              >
                Learn How it Works
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-12">
              {[
                { icon: Database, text: 'Player Career Profiles' },
                { icon: Users, text: 'Scout & Club Discovery' },
                { icon: Share2, text: 'Match Clips & Reports' }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-slate-200">
                  <item.icon className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium tracking-wide">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Featured Preview Overlay */}
          <div className="flex-1 w-full max-w-xl">
             <div className="surface-panel rounded-[40px] p-8 border border-white/10 shadow-2xl relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent pointer-events-none" />
                  <div className="relative space-y-6">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl font-bold">Rising Stars</h3>
                    <button onClick={onExplore} className="text-xs text-green-500 font-bold flex items-center gap-1 hover:gap-2 transition-all">
                      VIEW ALL <Globe className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
                    {featuredPlayers.length > 0 ? (
                      featuredPlayers.map((player) => (
                        <div
                          key={player.id}
                          className="surface-panel-low p-3 md:p-4 rounded-2xl border border-white/5 flex items-center justify-between gap-3 group/item hover:bg-white/5 transition-colors cursor-pointer min-h-0"
                          onClick={onExplore}
                        >
                          <div className="flex items-center gap-3 text-sm">
                            <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-200 uppercase">
                              {player.name.charAt(0)}
                            </div>
                            <div className="leading-tight">
                              <p className="text-xs md:text-sm font-bold text-white group-hover/item:text-green-400 transition-colors truncate max-w-[120px]">
                                {player.name}
                              </p>
                              <p className="text-[9px] md:text-[10px] text-slate-300 uppercase tracking-[0.3em]">
                                {player.main_position || 'N/A'}
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-300 text-sm text-center py-8 italic font-light">Fetching local talent data...</p>
                    )}
                  </div>
                </div>
             </div>
          </div>
        </section>

        {/* Features */}
        <section className="space-y-10">
          <div className="text-center space-y-3">
            <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Features</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold">Everything Scouta is built to do</h2>
            <p className="text-slate-200 max-w-2xl mx-auto">
              Scouta is designed as a complete football ecosystem — players own their careers, Scouts discover talent, and Clubs recruit with confidence.
            </p>
            <p className="text-xs uppercase tracking-[0.28em] text-slate-400">
              Match records and player stats must be tied to recognized competitions only.
            </p>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
           <div className="surface-panel-low p-8 rounded-3xl border border-white/10 space-y-4 hover:border-green-500/30 transition-colors">
              <div className="w-12 h-12 rounded-2xl bg-green-500/10 flex items-center justify-center text-green-500 mb-4">
                <Activity className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Player Career Management</h3>
              <p className="text-sm text-slate-200 leading-relaxed">
                Players build complete profiles with stats, career history, match clips, achievements, and documents — all in one place.
              </p>
           </div>
           <div className="surface-panel-low p-8 rounded-3xl border border-white/10 space-y-4 hover:border-blue-500/30 transition-colors">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500 mb-4">
                <Layers3 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Talent Discovery</h3>
              <p className="text-sm text-slate-200 leading-relaxed">
                Scouts and Clubs search, filter, and discover players by position, age, stats, availability, and match footage.
              </p>
           </div>
           <div className="surface-panel-low p-8 rounded-3xl border border-white/10 space-y-4 hover:border-purple-500/30 transition-colors">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center text-purple-500 mb-4">
                <BarChart3 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Scouting Reports</h3>
              <p className="text-sm text-slate-200 leading-relaxed">
                Create detailed reports with technical, tactical, physical, and mental ratings. Export and share with your network.
              </p>
           </div>
           <div className="surface-panel-low p-8 rounded-3xl border border-white/10 space-y-4 hover:border-orange-500/30 transition-colors">
              <div className="w-12 h-12 rounded-2xl bg-orange-500/10 flex items-center justify-center text-orange-500 mb-4">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Trial Invitations</h3>
              <p className="text-sm text-slate-200 leading-relaxed">
                Clubs send trial invitations directly to players. Players accept or decline — all managed within the platform.
              </p>
           </div>
          </div>
        </section>

        {/* Demo Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Demo</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold">See Scouta in Action</h2>
            <p className="text-slate-200 max-w-2xl mx-auto">
              Watch how players, scouts, and clubs use Scouta to manage careers, discover talent, and recruit with data.
            </p>
          </div>
          <div className="rounded-[40px] overflow-hidden border border-white/10 shadow-2xl">
            <div className="relative w-full bg-black" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/watch?si=nzDo8jO5gI-MmN3c&v=hBVAyLXQFRk&feature=youtu.be"
                title="Scouta Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        {/* Why Scouta? */}
        <section className="grid md:grid-cols-3 gap-8">
           <div className="surface-panel-low p-8 rounded-3xl border border-white/10 space-y-4 hover:border-green-500/30 transition-colors">
              <div className="w-12 h-12 rounded-2xl bg-green-500/10 flex items-center justify-center text-green-500 mb-4">
                <Database className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Player-First Design</h3>
              <p className="text-sm text-slate-200 leading-relaxed">
                Players own and manage their football careers — profile, stats, clips, achievements, and documents.
              </p>
           </div>
           <div className="surface-panel-low p-8 rounded-3xl border border-white/10 space-y-4 hover:border-blue-500/30 transition-colors">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500 mb-4">
                <Globe className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Built for Nigeria</h3>
              <p className="text-sm text-slate-200 leading-relaxed">
                Pre-seeded with NPFL, NNL, NLO, NWFL, and local competitions. Designed for Nigerian football's unique ecosystem.
              </p>
           </div>
           <div className="surface-panel-low p-8 rounded-3xl border border-white/10 space-y-4 hover:border-purple-500/30 transition-colors">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center text-purple-500 mb-4">
                <Share2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Direct Communication</h3>
              <p className="text-sm text-slate-200 leading-relaxed">
                Scouts and Clubs message players directly. Trial invitations, messaging, and profile sharing — all in one platform.
              </p>
           </div>
        </section>

        {/* Mini About/Mission */}
        <section className="text-center space-y-8 bg-[#0a151f] rounded-[48px] p-12 md:p-20 border border-white/10">
           <h2 className="text-3xl md:text-5xl font-display font-bold max-w-3xl mx-auto leading-tight italic">
             "Where players own their careers, and talent finds its path."
           </h2>
           <button 
             onClick={() => onNavigate('about')} 
             className="text-green-500 font-bold uppercase tracking-[0.2em] text-sm hover:underline"
           >
             Read Our Story →
           </button>
        </section>
      </div>
    </div>
  )
}

export default LandingPage
